import React from 'react';
import { startOfDay, endOfDay, addDays, isBefore, isAfter, isEqual, startOfMonth, addMonths } from 'date-fns';
import { Box, Heading, Text, VStack } from '@chakra-ui/react';
import { Event } from '../Event';
import { IEvent } from '../../../lib';

interface IAccessors {
	start: (event: IEvent) => Date;
	end: (event: IEvent) => Date;
	title: (event: IEvent) => string;
	allDay: (event: IEvent) => boolean;
}

interface ILocalizer {
	format: (date: Date | { start: Date; end: Date }, format: string) => string;
}

interface IAgendaViewProps {
	accessors: IAccessors;
	localizer: ILocalizer;
	length: number;
	date: Date;
	events: IEvent[];
}

function rangeFunc(start: Date, end: Date): Date[] {
	const days: Date[] = [];
	let current = start;
	while (isBefore(current, end) || isEqual(current, end)) {
		days.push(current);
		current = addDays(current, 1);
	}
	return days;
}

function inRange(e: IEvent, start: Date, end: Date, accessors: IAccessors): boolean {
	const eStart = startOfDay(accessors.start(e));
	const eEnd = accessors.end(e);
	const startsBeforeEnd = isBefore(eStart, end) || isEqual(eStart, end);
	const endsAfterStart = !isEqual(eStart, eEnd)
		? isAfter(eEnd, start)
		: !isBefore(eEnd, start) || isEqual(eEnd, start);
	return startsBeforeEnd && endsAfterStart;
}

export const Agenda = ({ accessors, localizer, length, date, events }: IAgendaViewProps) => {
	const renderDay = (day: Date, events: IEvent[]) => {
		return events.map((event, index) => (
			<VStack key={index} spacing={4} align='start' my={2}>
				{index === 0 && (
					<Heading as='h4' fontSize='lg' fontWeight='bold' mt={4} mb={-2}>
						{localizer.format(day, 'MMMM dd')}
					</Heading>
				)}
				<Box m={0} p={0} width='full'>
					<Event event={event} />
				</Box>
			</VStack>
		));
	};

	const end = addDays(startOfMonth(date), length);
	const range = rangeFunc(startOfMonth(date), end);
	const filteredEvents = events.filter((event) => inRange(event, startOfMonth(date), end, accessors));
	filteredEvents.sort((a, b) => accessors.start(a).getTime() - accessors.start(b).getTime());

	return (
		<Box as='ul' className='agenda-view' p={4} overflow='scroll' listStyleType='none'>
			{filteredEvents.length !== 0 ? (
				range.map((day, index) => {
					const dayDate = localizer.format(day, 'MMM-dd');
					const events = filteredEvents.filter((e) => inRange(e, startOfDay(day), endOfDay(day), accessors));
					return (
						events.length > 0 && (
							<Box as='li' className={`${dayDate} agenda-view__day`} key={index}>
								{renderDay(day, events)}
							</Box>
						)
					);
				})
			) : (
				<Text>No event dates in range</Text>
			)}
		</Box>
	);
};

Agenda.title = (start: Date, { localizer }: { localizer: ILocalizer }) => {
	const end = addMonths(start, 1);
	return localizer.format({ start, end }, 'agendaHeaderFormat');
};

Agenda.navigate = (date: Date, action: 'PREV' | 'NEXT') => {
	const sDate = startOfMonth(date);
	switch (action) {
		case 'PREV':
			return addMonths(sDate, -1);
		case 'NEXT':
			return addMonths(sDate, 1);
		default:
			return date;
	}
};
